import { useState, useEffect } from 'react'
import { FiMail, FiCheckCircle } from 'react-icons/fi'
import axios from 'axios'
import { DateTime } from 'luxon'

export default function SentRemindersWidget({ userTimezone = 'UTC' }) {
  const [sentReminders, setSentReminders] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchSentReminders()
    // Refresh every minute to remove reminders after their scheduled date passes
    const interval = setInterval(fetchSentReminders, 60000)
    return () => clearInterval(interval)
  }, [userTimezone])

  const fetchSentReminders = async () => {
    try {
      setLoading(true)
      
      // Try to fetch from the API endpoint
      let response
      try {
        // Base URL is configured as .../api, so using /reminders/visible avoids /api/api duplication
        response = await axios.get('/reminders/visible', {
          params: { sortBy: 'newest', limit: 100, offset: 0 }
        })
      } catch (err) {
        console.warn('Failed to fetch from /reminders/visible:', err.message)
        // Fallback to full prefixed path if baseURL is not set properly
        response = await axios.get('/api/reminders/visible', {
          params: { sortBy: 'newest', limit: 100, offset: 0 }
        })
      }

      console.log('✅ API Response received:', response.data) // Debug log

      // The API already applies all display rules:
      // 1. Only sent reminders (emailReminderSentStartOfDay = true)
      // 2. Within visibility window (00:00 to 23:59:59 on scheduled date)
      // 3. Auto-expired reminders are excluded
      // 4. Missed reminders are included with wasMissedRecovery flag
      
      const visibleReminders = response.data.data?.reminders || []
      console.log('📊 Visible reminders count:', visibleReminders.length) // Debug log
      console.log('📋 Reminders data:', visibleReminders) // Debug log
      
      // Log individual reminder details for debugging
      visibleReminders.forEach((reminder, idx) => {
        console.log(`Reminder ${idx + 1}:`, {
          title: reminder.title,
          scheduledDate: reminder.scheduledDate,
          sentAt: reminder.sentAt,
          sentAtFormatted: reminder.sentAtFormatted,
          timezone: reminder.timezone
        })
      })

      setSentReminders(visibleReminders)
    } catch (error) {
      console.error('❌ Error fetching sent reminders:', error.message)
      if (error.response) {
        console.error('Error response data:', error.response.data)
        console.error('Error status:', error.response.status)
      }
      console.error('Full error:', error)
      setSentReminders([]) // Clear reminders on error
    } finally {
      setLoading(false)
    }
  }

  const formatScheduledDate = (reminder) => {
    try {
      // The API returns scheduledDate as YYYY-MM-DD string
      // stored in user's local timezone (no UTC conversion needed)
      if (!reminder.scheduledDate) {
        return 'No scheduled date'
      }
      
      const tz = reminder.timezone || 'UTC'
      
      // scheduledDate is in YYYY-MM-DD format stored as user's local date
      // Parse it as a local date in the user's timezone
      const parsedDate = DateTime.fromISO(reminder.scheduledDate, { zone: tz })
      
      if (!parsedDate.isValid) {
        console.warn('Invalid scheduled date:', reminder.scheduledDate)
        return 'Unknown date'
      }
      
      return parsedDate.toFormat('MMM d, yyyy')
    } catch (error) {
      console.error('Error formatting scheduled date:', error, reminder)
      return 'Unknown date'
    }
  }

  const formatSentDate = (reminder) => {
    try {
      // sentAtFormatted is already formatted by the backend in user's timezone
      if (reminder.sentAtFormatted) {
        return reminder.sentAtFormatted
      }
      
      // Fallback: format from sentAt ISO string
      if (!reminder.sentAt) {
        return 'Not sent yet'
      }
      
      const utcDate = new Date(reminder.sentAt)
      if (isNaN(utcDate.getTime())) {
        console.warn('Invalid sent date:', reminder.sentAt)
        return 'Unknown time'
      }
      
      const tz = reminder.timezone || 'UTC'
      const formatted = DateTime
        .fromJSDate(utcDate, { zone: 'UTC' })
        .setZone(tz)
        .toFormat("MMM d 'at' HH:mm zzz")
      
      return formatted
    } catch (error) {
      console.error('Error formatting sent date:', error, reminder)
      return 'Unknown time'
    }
  }

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
        <div className="flex items-center gap-2 text-green-700">
          <FiMail className="w-5 h-5" />
          <span className="text-sm">Loading sent reminders...</span>
        </div>
      </div>
    )
  }

  if (sentReminders.length === 0) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
        <div className="flex items-center gap-2 text-green-700">
          <FiMail className="w-5 h-5" />
          <span className="text-sm">No sent reminders yet</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 overflow-hidden">
      <div className="p-4">
        <div className="flex items-center gap-2 text-green-700 mb-3">
          <FiCheckCircle className="w-5 h-5" />
          <span className="text-sm font-semibold">
            Reminder Emails Sent ({sentReminders.length})
          </span>
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {sentReminders.map(reminder => (
            <div
              key={reminder.id}
              className="bg-white rounded border border-green-100 p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm truncate">
                    {reminder.title}
                  </h4>

                  <p className="text-xs text-gray-600 mt-1">
                    <span className="font-medium">Scheduled:</span>{' '}
                    {formatScheduledDate(reminder)}
                  </p>

                  {reminder.sentAt && (
                    <p className="text-xs text-green-700 mt-1">
                      <span className="font-medium">✓ Sent:</span>{' '}
                      {formatSentDate(reminder)}
                    </p>
                  )}

                  {reminder.wasMissedRecovery && (
                    <p className="text-xs text-amber-600 mt-1">
                      <span className="font-medium">⚠ Sent later (recovery):</span> System recovered from downtime
                    </p>
                  )}
                </div>
              </div>

              {/* Time remaining indicator */}
              {reminder.timeRemaining && (
                <div className="mt-2 pt-2 border-t border-green-100">
                  <p className="text-xs text-green-600 font-medium">
                    {reminder.displayStatus}
                  </p>
                  <div className="w-full bg-green-100 rounded-full h-1.5 mt-1">
                    <div
                      className="bg-green-500 h-1.5 rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, (reminder.timeRemaining.seconds / (24 * 3600)) * 100)}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-green-600 mt-3 text-center">
          Reminders display from 00:00 to 23:59:59 on scheduled date
        </p>
      </div>
    </div>
  )
}
